import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { useEffect, useRef } from 'react';
import { type FieldArrayPath, type FieldPathByValue, type FieldValues, type Path, type PathValue, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InstitutionFieldCountry } from '@/features/institution/components/fields/country';
import { InstitutionFieldsetAddress } from '@/features/institution/components/fieldsets/address';
import { InstitutionFieldsetDate } from '@/features/institution/components/fieldsets/date';
import type { InstitutionAddress } from '@/features/institution/schemas/address';
import type { InstitutionPerson } from '@/features/institution/schemas/person';
import { TaxResidencyFieldset } from '@/features/tax-residency/components/fieldset';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { getCountryPrimaryPhoneCode } from '@/services/api/mainstream';
import type { DateParts } from '@/utils/date';

type InstitutionFieldsetPersonProps<T extends FieldValues> = {
  // Controlling persons are reported for tax only, so they are not asked for contact details
  isContactable?: boolean;
  name: FieldPathByValue<T, InstitutionPerson>;
};

const InstitutionFieldsetPerson = <T extends FieldValues>({ isContactable = true, name }: InstitutionFieldsetPersonProps<T>) => {
  const form = useFormContext<T>();
  const { data: countries } = useMainstreamCountries();
  const { t } = useTranslation();
  const addressCountry: string = form.watch(`${name}.address.country` as Path<T>);
  const prefilledAreaCode = useRef('');

  useEffect(() => {
    const areaCodeName = `${name}.phoneNumber.areaCode` as Path<T>;
    const areaCode: string = form.getValues(areaCodeName);
    const country = countries?.[addressCountry];
    const phoneCode = (country && getCountryPrimaryPhoneCode(country)) || '';
    const isTypedOver = areaCode !== '' && areaCode !== prefilledAreaCode.current;

    if (!isContactable || isTypedOver || phoneCode === areaCode) return;

    prefilledAreaCode.current = phoneCode;
    form.setValue(areaCodeName, phoneCode as PathValue<T, Path<T>>, { shouldValidate: form.formState.isSubmitted });
  }, [addressCountry, countries, form.formState.isSubmitted, form.getValues, form.setValue, isContactable, name]);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField
          control={form.control}
          name={`${name}.firstName` as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.person.firstName')}*</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('institutionDetails.person.placeholders.firstName')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${name}.lastName` as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.person.lastName')}*</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('institutionDetails.person.placeholders.lastName')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <InstitutionFieldsetDate<T> legend={t('institutionDetails.person.dateOfBirth')} name={`${name}.dateOfBirth` as FieldPathByValue<T, DateParts>} />
      <InstitutionFieldCountry<T>
        label={t('institutionDetails.person.countryOfBirth')}
        name={`${name}.countryOfBirth` as FieldPathByValue<T, string>}
        placeholder={t('institutionDetails.person.placeholders.countryOfBirth')}
      />
      <FormField
        control={form.control}
        name={`${name}.localityOfBirth` as Path<T>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.person.localityOfBirth')}*</FormLabel>
            <FormControl>
              <Input {...field} placeholder={t('institutionDetails.person.placeholders.localityOfBirth')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <InstitutionFieldCountry<T>
        label={t('institutionDetails.person.nationality')}
        name={`${name}.nationality` as FieldPathByValue<T, string>}
        placeholder={t('institutionDetails.person.placeholders.nationality')}
      />
      {isContactable ? (
        <>
          <FormField
            control={form.control}
            name={`${name}.email` as Path<T>}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.person.email')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.person.placeholders.email')} type="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-3 gap-6">
            <FormField
              control={form.control}
              name={`${name}.phoneNumber.areaCode` as Path<T>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.person.areaCode')}*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('institutionDetails.person.placeholders.areaCode')} type="tel" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`${name}.phoneNumber.mobilePhone` as Path<T>}
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>{t('institutionDetails.person.mobilePhone')}*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('institutionDetails.person.placeholders.mobilePhone')} type="tel" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </>
      ) : null}
      <fieldset className="space-y-6">
        <legend className="font-medium">{t('institutionDetails.person.address')}</legend>
        <InstitutionFieldsetAddress<T> name={`${name}.address` as FieldPathByValue<T, InstitutionAddress>} />
      </fieldset>
      <fieldset className="space-y-6">
        <legend className="font-medium">{t('institutionDetails.person.taxResidences')}</legend>
        <TaxResidencyFieldset<T> name={`${name}.taxResidences` as FieldArrayPath<T>} subject="individual" />
      </fieldset>
    </div>
  );
};
InstitutionFieldsetPerson.displayName = 'InstitutionFieldsetPerson';

export { InstitutionFieldsetPerson };
